import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import Modal from "@/components/admin/Modal";
import { Field, TextInput, TextArea, Toggle } from "@/components/admin/FormControls";
import { Plus, Pencil, Trash2, Save, BookOpen, Sparkles, Loader2 } from "lucide-react";

const emptyPlan = {
    name: "", description: "", amount: 0, currency: "brl",
    interval_days: 30, highlights: [], badge: "", order: 99, active: true,
};

function BRL(value) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
}

function HighlightsEditor({ value, onChange, testIdPrefix }) {
    const list = Array.isArray(value) ? value : [];
    const update = (i, v) => onChange(list.map((h, idx) => (idx === i ? v : h)));
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    const add = () => onChange([...list, ""]);
    return (
        <div className="space-y-2">
            {list.map((h, i) => (
                <div key={i} className="flex gap-2">
                    <input
                        data-testid={`${testIdPrefix}-${i}`}
                        value={h}
                        onChange={(e) => update(i, e.target.value)}
                        placeholder={`Benefício #${i + 1}`}
                        className="imq-input flex-1"
                    />
                    <button type="button" onClick={() => remove(i)} className="px-3 rounded-xl border border-black/5 hover:bg-red-50 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            ))}
            <button type="button" onClick={add} data-testid={`${testIdPrefix}-add`} className="text-sm font-display text-[#5C5C5C] hover:text-[#1A1A1A] inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Adicionar benefício
            </button>
        </div>
    );
}

// ============================================================
// PLANS TAB
// ============================================================
function PlansTab() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyPlan);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/plans").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(emptyPlan); setEditing(null); setError(""); setOpen(true); };
    const openEdit = (p) => { setForm({ ...emptyPlan, ...p, highlights: [...(p.highlights || [])] }); setEditing(p.id); setError(""); setOpen(true); };
    const save = async () => {
        setError(""); setSaving(true);
        try {
            const payload = { ...form, highlights: form.highlights.filter((h) => h?.trim()) };
            if (editing) await api.put(`/admin/plans/${editing}`, payload);
            else await api.post("/admin/plans", payload);
            setOpen(false); await load();
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    };
    const remove = async (id) => {
        if (!window.confirm("Excluir este plano?")) return;
        try { await api.delete(`/admin/plans/${id}`); await load(); }
        catch (e) { alert(formatApiError(e)); }
    };

    return (
        <section data-testid="plans-tab">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display text-2xl tracking-tight">Planos de assinatura</h2>
                    <p className="font-body text-sm text-[#5C5C5C] mt-1">Defina valores, intervalos e benefícios exibidos em /planos.</p>
                </div>
                <button onClick={openCreate} data-testid="plan-create-btn" className="btn-primary">
                    <Plus className="w-4 h-4" /> Novo plano
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
                {items.map((p) => (
                    <article key={p.id} data-testid={`plan-card-${p.id}`} className="bg-white border border-black/5 rounded-2xl p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="overline text-[#5C5C5C]">{p.id} · {p.active ? "Ativo" : "Inativo"}</div>
                                <div className="font-display text-2xl mt-1 tracking-tight">{p.name}</div>
                                <div className="font-display text-3xl font-light mt-3">{BRL(p.amount)}<span className="text-sm font-body text-[#5C5C5C]"> / {p.interval_days} dias</span></div>
                                {p.badge && <div className="inline-block mt-2 px-2 py-1 rounded-full bg-[#1A1A1A] text-white text-[10px] tracking-widest font-display">{p.badge}</div>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <button onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`} className="p-2 rounded-lg hover:bg-black/5 transition-colors"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => remove(p.id)} data-testid={`plan-delete-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                        <p className="font-body text-sm text-[#5C5C5C] mt-3 line-clamp-2">{p.description}</p>
                        <ul className="mt-4 space-y-1">
                            {(p.highlights || []).slice(0, 4).map((h, i) => (
                                <li key={i} className="font-body text-xs text-[#5C5C5C]">· {h}</li>
                            ))}
                        </ul>
                    </article>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-12 font-body text-[#5C5C5C] bg-white rounded-2xl border border-black/5">Nenhum plano cadastrado.</div>
                )}
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar plano" : "Novo plano"} size="lg">
                <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Nome"><TextInput testId="plan-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
                        <Field label="Badge (opcional)" hint="Aparece como selo no card"><TextInput testId="plan-badge" value={form.badge} onChange={(v) => setForm({ ...form, badge: v })} /></Field>
                    </div>
                    <Field label="Descrição"><TextArea testId="plan-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={2} /></Field>
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Valor (R$)"><TextInput testId="plan-amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} min={0} /></Field>
                        <Field label="Moeda"><TextInput testId="plan-currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v.toLowerCase() })} /></Field>
                        <Field label="Duração (dias)" hint="30 = mensal, 365 = anual"><TextInput testId="plan-interval" type="number" value={form.interval_days} onChange={(v) => setForm({ ...form, interval_days: v })} min={1} /></Field>
                    </div>
                    <Field label="Benefícios (highlights)"><HighlightsEditor value={form.highlights} onChange={(v) => setForm({ ...form, highlights: v })} testIdPrefix="plan-highlight" /></Field>
                    <div className="flex items-center justify-between pt-2">
                        <Toggle testId="plan-active" checked={!!form.active} onChange={(v) => setForm({ ...form, active: v })} label={form.active ? "Plano ativo (visível em /planos)" : "Plano desativado"} />
                        <Field label="Ordem"><TextInput testId="plan-order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} min={1} /></Field>
                    </div>
                    {error && <div data-testid="plan-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={() => setOpen(false)} className="btn-ghost">Cancelar</button>
                        <button onClick={save} data-testid="plan-save" className="btn-primary" disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
                        </button>
                    </div>
                </div>
            </Modal>
        </section>
    );
}

// ============================================================
// UPSELL SETTINGS
// ============================================================
function UpsellSettingsCard() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => { api.get("/admin/settings/upsell").then((r) => setData(r.data)); }, []);

    const save = async () => {
        setError(""); setSaving(true);
        try {
            const { data: out } = await api.put("/admin/settings/upsell", {
                discount_pct: Number(data.discount_pct),
                window_minutes: Number(data.window_minutes),
                title: data.title || "",
            });
            setData(out);
            setSavedAt(new Date());
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    };

    if (!data) return null;
    return (
        <section data-testid="upsell-settings" className="mt-16">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                    <h2 className="font-display text-2xl tracking-tight">Oferta de upsell</h2>
                    <p className="font-body text-sm text-[#5C5C5C]">Aplicado aos e-books logo após a confirmação de pagamento de um plano.</p>
                </div>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl p-6 grid md:grid-cols-3 gap-5">
                <Field label="Desconto (%)" hint="0 desliga o desconto"><TextInput testId="upsell-discount" type="number" value={data.discount_pct} onChange={(v) => setData({ ...data, discount_pct: v })} min={0} max={90} /></Field>
                <Field label="Janela (min)" hint="Tempo do contador após o pagamento"><TextInput testId="upsell-window" type="number" value={data.window_minutes} onChange={(v) => setData({ ...data, window_minutes: v })} min={1} /></Field>
                <Field label="Título da oferta"><TextInput testId="upsell-title" value={data.title || ""} onChange={(v) => setData({ ...data, title: v })} placeholder="Aprofunde sua jornada com e-books" /></Field>
                <div className="md:col-span-3 flex items-center justify-between pt-1">
                    <div className="text-sm font-body text-[#5C5C5C]">
                        {savedAt && <span data-testid="upsell-saved">Salvo às {savedAt.toLocaleTimeString("pt-BR")}</span>}
                        {error && <span data-testid="upsell-error" className="text-red-600">{error}</span>}
                    </div>
                    <button onClick={save} data-testid="upsell-save" className="btn-primary" disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar configuração
                    </button>
                </div>
            </div>
        </section>
    );
}

// ============================================================
// PAGE
// ============================================================
export default function AdminPlans() {
    return (
        <AdminLayout eyebrow="Monetização" title="Planos & Upsell">
            <PlansTab />
            <UpsellSettingsCard />
            <div className="mt-6 flex items-start gap-2 text-xs text-[#5C5C5C] font-body bg-[#FAFAF7] rounded-xl p-4 border border-black/5">
                <BookOpen className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                    Os <a href="/admin/biblioteca" className="font-display text-[#1A1A1A] hover:underline">e-books cadastrados na Biblioteca</a> são automaticamente oferecidos na sessão de upsell logo após o pagamento de um plano, com o desconto configurado acima.
                </div>
            </div>
        </AdminLayout>
    );
}
